/**
 * LocalStorage-backed toggles for database performance diagnostics.
 * The skeleton overlay is a visual aid only; the underlying
 * `database-virtualized-rows-render-time` metric runs unconditionally.
 */
const SKELETON_OVERLAY_KEY = 'databases-perf-tracing.skeleton-overlay';
const ACTION_TRACING_KEY = 'databases-perf-tracing.action-tracing';
const CHANGE_EVENT = 'databases-perf-tracing:change';

function readBoolean(key: string): boolean {
  try {
    return globalThis.localStorage?.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function writeBoolean(key: string, value: boolean): void {
  try {
    globalThis.localStorage?.setItem(key, value ? 'true' : 'false');
  } catch {
    // Best-effort: localStorage may be unavailable (SSR, blocked).
  }
}

function emitChange(): void {
  try {
    globalThis.dispatchEvent?.(new Event(CHANGE_EVENT));
  } catch {
    // No-op outside the browser.
  }
}

export function isSkeletonOverlayEnabled(): boolean {
  return readBoolean(SKELETON_OVERLAY_KEY);
}

export function setSkeletonOverlayEnabled(value: boolean): void {
  writeBoolean(SKELETON_OVERLAY_KEY, value);
  emitChange();
}

export function isActionTracingEnabled(): boolean {
  return readBoolean(ACTION_TRACING_KEY);
}

export function setActionTracingEnabled(value: boolean): void {
  writeBoolean(ACTION_TRACING_KEY, value);
  emitChange();
}

/**
 * Subscribe to performance diagnostics toggle changes. Returns an unsubscribe
 * function.
 */
export function onPerfTracingChange(listener: () => void): () => void {
  const handler = () => listener();
  try {
    globalThis.addEventListener?.(CHANGE_EVENT, handler);
  } catch {
    return () => {};
  }
  return () => {
    try {
      globalThis.removeEventListener?.(CHANGE_EVENT, handler);
    } catch {
      // No-op outside the browser.
    }
  };
}
